import React, { useEffect, useState } from "react";
import { I18nextProvider } from "react-i18next";
import {
  BrowserRouter as Router,
  Outlet,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import PreLoginLayoutRoute from "../layouts/preLogin";
import "../themes/out/base.css";
import i18n from "../translations";
import SecuredLogin from "../utils/SecuredLogin";
import { Toaster, toasterPositions } from "../widgets";
import Home from "./home";
import Login from "./login";
import SignUp from "./signup";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import Dashboard from "./dashboard";
import Profile from "./profile";
import PostLoginLayoutRoute from "../layouts/postLogin";
import Project from "./project";
import CreateProject from "./createProject";
import { useDispatch, useSelector } from "react-redux";
import { CurrentUser, setAuthSuccess } from "../store/slices/auth";
import { fetchTenantConfig, selectInAppLogoUrl } from "../store/slices/tenantSlice";
import SampleSize from "./sampleSize";
import DataCollection from "./dataCollection";
import StudyModule from "./studyModule";
import Subscriptions from "./subscriptions";
import TopicsLibrary from "./TopicsLibrary";
import JournalSearch from "./journalSearch";
import Events from "./events";
import AddVariables from "./dataCollection/AddVariables";
import DataEntry from "./dataCollection/DataEntry";
import SurveyForm from "./surveyForm";
import CreateCriteria from "./dataCollection/CreateCriteria";
import CriteriaForm from "./dataCollection/CriteriaForm";
import People from "./people";
import PeopleProfile from "./people/PeopleProfile";
import PeopleInviteListMobile from "./people/PeopleInviteListMobile";
import PeopleProfileMobile from "./people/PeopleProfileMobile";
import InviteSuccessMobile from "./people/inviteSuccessMobile";
import PeopleListChat from "./people/PeopleListChat";
import PeopleInvite from "./people/PeopleInvite";
import PeopleInviteMobile from "./people/PeopleInviteMobile";
import PeopleChatMobile from "./people/PeopleChatMobile";
import InviteReject from "./people/InviteReject";
import ProjectMobile from "./projectmobile";
import Submissions from "./submissions";
import SubmissionInfo from "./submissions/SubmissionInfo";
import RegisteredEvents from "./submissions/RegisteredEvents";
import SubmissionDocument from "./submissions/SubmissionDocument";
import ProjectDetail from "./projectDetails";
import { usePWAInstall } from "react-use-pwa-install";
import { Modal } from "reactstrap";
import CreateProjectMobile from "./createProjectMobile";
import Success from "./createProjectMobile/Success";
import TitleGeneration from "./createProjectMobile/TitleGeneration";
import withClearCache from "../ClearCache";
import DataAnalysis from "./dataAnalysis";
import ProfileMobile from "./profileMobile";
import LearningModule from "./learning";
import CourseModules from "./learning/CourseModules";
import ModuleVideo from "./learning/ModuleVideo";
import DataAnalysisTable from "./dataAnalysis/DataAnalysisTable";
import ConfigChart from "./dataAnalysis/ConfigChart";
import AnalysisReport from "./dataAnalysis/AnalysisReport";
import AddGroupingMobile from "./dataAnalysis/AddGroupingMobile";
import Editor from "./Editor/Editor";
import Planner from "./planner";
import Documents from "./documents";
import VersionSelector from "./Editor/VersionSelector";
import * as serviceWorkerRegistration from '../serviceWorkerRegistration';
import ProjectDashboard from "./projectDashboard/ProjectDashboard";
import CertificatesPage from "./learning/CertificatesPage";
import DataEntryStatus from "./dataCollection/DataEntryStatus";
import LiteratureSearch from "./literatureSearch/LiteratureSearch";
import AiAgentChat from "./common/AiChatComponent/AiAgentChat";
import DeepSearch from "./deepSearch/DeepSearch";
import SurveyFormComponent from "./surveyForm/SurveyFormComponent";
import AiModule from "./aiModule/AiModule";
import QuickDashboard from "./dataCollection/QuickDashboard";
import { ConditionalRoot } from "./conditionalRoute";
import SpeechToTextDataEntry from "./dataCollection/SpeechToTextDataEntry";
import ConversationRecording from "./dataCollection/ConversationRecording";
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { getCookie, setCookie } from "../utils/Utils";

const ClearCacheComponent = withClearCache(MainApp);

function App() {
  return <ClearCacheComponent />;
}

function MainApp() {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(true);
  const tenantConfig = useSelector((state: any) => state.tenant.config);
  const inAppLogoUrl = useSelector(selectInAppLogoUrl);

  useEffect(() => {
    // Fetch tenant configuration dynamically based on hostname
    dispatch(fetchTenantConfig(window.location.hostname) as any);
  }, [dispatch]);

  useEffect(() => {
    if (tenantConfig?.jsonconfig?.branding) {
      const branding = tenantConfig.jsonconfig.branding;
      if (branding.primary_color) {
        let primaryColor = branding.primary_color;
        if (!primaryColor.startsWith('#')) {
          primaryColor = `#${primaryColor}`;
        }
        document.documentElement.style.setProperty('--primary-color', primaryColor);
        document.documentElement.style.setProperty('--primary-color-light', primaryColor + '1A');
        document.documentElement.style.setProperty('--primary-color-super-light', primaryColor + '05');
      }
      if (branding.font?.font_highlight_color) {
        let highlightColor = branding.font.font_highlight_color;
        if (!highlightColor.startsWith('#')) {
          highlightColor = `#${highlightColor}`;
        }
        document.documentElement.style.setProperty('--font-highlight-color', highlightColor);
      }
    }
  }, [tenantConfig]);

  useEffect(() => {
    if (tenantConfig && inAppLogoUrl) {
      const favicon = document.querySelector('link[rel="icon"]');
      if (favicon) {
        favicon.setAttribute('href', inAppLogoUrl);
      }
      const appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]');
      if (appleTouchIcon) {
        appleTouchIcon.setAttribute('href', inAppLogoUrl);
      }
    }
  }, [tenantConfig, inAppLogoUrl]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      const message = "Are you sure you want to leave this page?";
      event.preventDefault();
      event.returnValue = message;
      return message;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const initializeFingerprint = async () => {
      try {
        let device_id = getCookie('device_id');
        if (!device_id) {
          const fp = await FingerprintJS.load();
          const result = await fp.get();
          const visitorId = result.visitorId;
          setCookie('device_id', visitorId);
          device_id = visitorId;
        }
      } catch (error) {
        console.error('Error generating fingerprint:', error);
      }
    };

    initializeFingerprint();
  }, []);

  useEffect(() => {
    let userData = localStorage.getItem("coguide_user");
    try {
      if (userData) {
        let currentUser: CurrentUser = JSON.parse(userData);
        dispatch(setAuthSuccess(currentUser));
      }
    } catch (error) {
      console.error("Error parsing user data:", error);
    }

    if (window.innerWidth > 700) {
      localStorage.setItem("device", "desktop")
    } else {
      localStorage.setItem("device", "mobile")
    }
  }, [dispatch]);

  const install = usePWAInstall();

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      {install &&
        <Modal closable={false} visible={isModalOpen} okText={"Add"} onOk={install} onCancel={handleCancel} >
          Add {tenantConfig?.jsonconfig?.branding?.app_name || "Coguide"} to Home Screen?
        </Modal>
      }
      <I18nextProvider i18n={i18n}>
        <Toaster position={toasterPositions["TOP-RIGHT"]} />
        <Router>
          <Routes>
            {/* Pre-login routes */}
            <Route
              element={
                <PreLoginLayoutRoute>
                  <Outlet />
                </PreLoginLayoutRoute>
              }
            >
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<SignUp />} />
            </Route>

            {/* Public routes without layout */}
            <Route path="/surveyForm" element={<SurveyForm />} />
            <Route path="/version" element={<VersionSelector />} />

            {/* Protected routes with post-login layout */}
            <Route
              element={
                <SecuredLogin>
                  <PostLoginLayoutRoute>
                    <Outlet />
                  </PostLoginLayoutRoute>
                </SecuredLogin>
              }
            >
              {/* Conditional root - MUST be first to match before other routes */}
              <Route index element={<ConditionalRoot />} />

              <Route path="project" element={<Project />} />
              <Route path="project-dashboard" element={<ProjectDashboard />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="learning" element={<LearningModule />} />
              <Route path="ai-module" element={<AiModule />} />
              <Route path="profile" element={<Profile />} />
              <Route path="profilemobile" element={<ProfileMobile />} />
              <Route path="people" element={<People />} />
              <Route path="planner" element={<Planner />} />
              <Route path="literaturesearch" element={<LiteratureSearch />} />
              <Route path="deepsearch" element={<DeepSearch />} />
              <Route path="document" element={<Documents />} />
              <Route path="projectmobile" element={<ProjectMobile />} />
              <Route path="peopleprofile" element={<PeopleProfile />} />
              <Route path="home" element={<Home />} />
              <Route path="sampleSize" element={<SampleSize />} />
              <Route path="studyModule" element={<StudyModule />} />
              <Route path="submissioninfo" element={<SubmissionInfo />} />
              <Route path="registeredevents" element={<RegisteredEvents />} />
              <Route path="documentsubmission" element={<SubmissionDocument />} />
              <Route path="dataCollection" element={<DataCollection />} />
              <Route path="dataStatus" element={<DataEntryStatus />} />
              <Route path="speechToText" element={<SpeechToTextDataEntry />} />
              <Route path="speechToTextConversation" element={<ConversationRecording />} />
              <Route path="dataStatus" element={<DataEntryStatus />} />
              <Route path="quickDashboard" element={<QuickDashboard />} />
              <Route path="coursemodule" element={<CourseModules />} />
              <Route path="certificates" element={<CertificatesPage />} />
              <Route path="modulevideo" element={<ModuleVideo />} />
              <Route path="dataanalysis" element={<DataAnalysis />} />
              <Route path="subscriptions" element={<Subscriptions />} />
              <Route path="topics" element={<TopicsLibrary />} />
              <Route path="journal" element={<JournalSearch />} />
              <Route path="events" element={<Events />} />
              <Route path="submissions" element={<Submissions />} />
              <Route path="projectdetail" element={<ProjectDetail />} />
              <Route path="chart" element={<ConfigChart />} />
              <Route path="analysis" element={<AnalysisReport />} />
              <Route path="peopleinvitemobile" element={<PeopleInviteListMobile />} />
              <Route path="peopleinvite" element={<PeopleInviteMobile />} />
              <Route path="peopleprofilemobile" element={<PeopleProfileMobile />} />
              <Route path="invitesuccess" element={<InviteSuccessMobile />} />
              <Route path="peoplemobile" element={<PeopleListChat />} />
              <Route path="peoplechat" element={<PeopleChatMobile />} />
              <Route path="invitereject" element={<InviteReject />} />
              <Route path="titlegenerate" element={<TitleGeneration />} />
              <Route path="success" element={<Success />} />
              <Route path="editor" element={<Editor />} />
              <Route path="addVariables" element={<AddVariables />} />
              <Route path="createProject" element={<CreateProject />} />
              <Route path="createprojectmobile" element={<CreateProjectMobile />} />
              <Route path="dataEntry" element={<SurveyFormComponent />} />
              <Route path="createCriteria" element={<CreateCriteria />} />
              <Route path="criteriaForm" element={<CriteriaForm />} />
            </Route>

            {/* Catch all - redirect to dashboard */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>

          <AiAgentChat />
        </Router>
      </I18nextProvider>
    </>
  );
}

export default App;